import React, { useState } from 'react';
import styled from 'styled-components';
import media from 'styled-media-query';
import { Box, Col, Container, Flex, Link, Row } from '@firsttable/web-components/atoms';
import RegionsModal from '@firsttable/web-components/organisms/RegionsModal';
import spaces from '@firsttable/web-theme/styledSpaces';
import { breakpointsObject as bp, useIsTablet } from '@firsttable/web-theme';
import { GroupRegionType } from '@firsttable/types';
import dynamic from 'next/dynamic';
import IconBirdLogo from '@firsttable/web-components/atoms/Icon/IconBirdLogo';
import IconLogo from '@firsttable/web-components/atoms/Icon/IconLogo';
import IconArrowDown from '@firsttable/web-components/atoms/Icon/IconArrowDown';
import IconSearch from '@firsttable/web-components/atoms/Icon/IconSearch';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import withTrackableNavLinkLinkComponent from '@/components/analytics/hocs/withTrackableNavLinkComponent';
import withMouseDown from '@/components/analytics/hocs/withMouseDown';
import HowItWorksModal from '@/components/organisms/HowItWorksModal';
import UserMenu, { ACCOUNT_CORNER_WIDTH } from './UserMenu';
import { RegionFilterWrapper, SearchBarWrapper, SearchBtn, SearchBtnText } from './styles';
import GatsbyLinkConvertor from '../../GatsbyLinkConvertor';
import { isMinorSite } from '../../../data';
import { usePeriodicMemberRefresh } from '../../hooks/useMemberRefresh';

const SearchAllBar = dynamic(() => import('./SearchAllBar'));

type PureMenuProps = {
  onlyLogo?: boolean;
  menuTitle?: string;
  transparent?: boolean;
  logOut?: (t?: any) => void;
  auth?: {
    isLoggedIn: boolean;
  };
  groupedRegions?: GroupRegionType;
  regionsList?: Record<string, never>;
};

const DesktopRegionFilterWrapper = styled(Box)`
  display: none;
  ${media.greaterThan(bp.m)`
    display: flex;
    gap: 5px;
  `};
`;

const MobileMenuWrapper = styled(Box)`
  display: flex;
  gap: 5px;
  ${media.greaterThan(bp.m)`
    display: none;
  `};
`;

const RefreshingUserMenu = (props) => {
  usePeriodicMemberRefresh({ pollIntervalMinutes: 10 });
  return <UserMenu {...props} />;
};

// Reset the global min-width to auto
const MenuWrap = styled(Box)`
  * {
    min-width: auto;
  }
`;

const PureMenu = ({
  onlyLogo = false,
  transparent = false,
  logOut,
  auth,
  groupedRegions,
  regionsList,
  menuTitle = '',
  ...props
}: PureMenuProps) => {
  const [showSearch, setShowSearch] = useState(false);
  const toggleSearchBar = () => setShowSearch(!showSearch);
  const isTablet = useIsTablet();
  const height = {
    desktop: transparent ? '116px' : '84px',
    mobile: '52px',
  };
  const menuColour = transparent ? 'white' : 'brand.blue';

  const searchText = 'Search locations or restaurants';

  const SearchBtnTextWithTracking = withMouseDown(
    SearchBtnText,
    'search bar',
    'toggle',
    searchText
  );

  const SearchBtnWithTracking = withMouseDown(SearchBtn, 'search bar', 'toggle', searchText);

  return (
    <TrackProvider section="header">
      <MenuWrap
        {...spaces(props)}
        bg={['transparent', transparent && 'transparent']}
        position="relative"
        zIndex={5}
        boxShadow={
          onlyLogo
            ? null
            : ['0px 30px 20px rgba(0, 0, 0, 0.03)', '0px 30px 60px rgba(0, 0, 0, 0.03)']
        }>
        <Container maxWidth="100%" width="auto">
          <Row>
            <Col>
              <Flex justifyContent={onlyLogo ? 'center' : null} position="relative">
                <Flex
                  height={[height.mobile, height.desktop]}
                  alignItems="center"
                  position="relative"
                  zIndex={1}
                  flex={onlyLogo ? 0 : [1, 1, 0]}>
                  <TrackProvider link_copy="first table" link_name="first table home">
                    <Link
                      href="/"
                      NavComponent={GatsbyLinkConvertor}
                      display="flex"
                      height="100%"
                      aria-label="go home"
                      color={menuColour}>
                      <DesktopRegionFilterWrapper
                        alignSelf="center"
                        flexDirection="row"
                        alignItems="center">
                        <IconBirdLogo color={null} top={2} />
                        <IconLogo color={null} top={2} />
                      </DesktopRegionFilterWrapper>
                      <MobileMenuWrapper alignSelf="center" flexDirection="row" alignItems="center">
                        <IconBirdLogo color={null} top={2} />
                        {onlyLogo && <IconLogo color={null} top={2} />}
                      </MobileMenuWrapper>
                    </Link>
                  </TrackProvider>
                  {!onlyLogo && (
                    <MobileMenuWrapper>
                      <RegionFilterWrapper height={height}>
                        <RegionsModal
                          role="button"
                          aria-label="region"
                          color={transparent ? 'white' : 'brand.blue'}
                          heights={height}
                          zIndex={!showSearch ? 1 : null}
                          pl={5}
                          useButton={false}
                          modalNumber={2}
                          groupedRegions={groupedRegions}
                          regionsList={regionsList}
                          NavLinkComponent={withTrackableNavLinkLinkComponent({
                            section: 'region modal',
                          })(GatsbyLinkConvertor)}>
                          <Box ml="3px" mr="6px" fontSize="15px">
                            {menuTitle || 'Region'}
                          </Box>
                          <IconArrowDown color="inherit" />
                        </RegionsModal>
                      </RegionFilterWrapper>
                    </MobileMenuWrapper>
                  )}
                </Flex>
                {!onlyLogo && (
                  <>
                    <DesktopRegionFilterWrapper>
                      <RegionFilterWrapper height={height}>
                        {!transparent ? (
                          <RegionsModal
                            role="button"
                            aria-label="region"
                            color="brand.blue"
                            heights={height}
                            zIndex={!showSearch ? 1 : null}
                            useButton={false}
                            modalNumber={1}
                            groupedRegions={groupedRegions}
                            regionsList={regionsList}
                            NavLinkComponent={withTrackableNavLinkLinkComponent({
                              section: 'region modal',
                            })(GatsbyLinkConvertor)}>
                            <Box mr="6px">Region</Box>
                            <IconArrowDown color="inherit" />
                          </RegionsModal>
                        ) : (
                          <RegionsModal
                            groupedRegions={groupedRegions}
                            regionsList={regionsList}
                            zIndex={!showSearch ? 1 : null}
                            useButton
                            NavLinkComponent={withTrackableNavLinkLinkComponent({
                              section: 'region modal',
                            })(GatsbyLinkConvertor)}>
                            Regions
                            <IconArrowDown ml="5px" mt="-3px" color="inherit" />
                          </RegionsModal>
                        )}
                      </RegionFilterWrapper>
                    </DesktopRegionFilterWrapper>
                    <div className="flex sm:hidden">
                      <TrackProvider link_type="navigation" link_name="how it works">
                        <HowItWorksModal transparent={transparent} />
                      </TrackProvider>
                    </div>
                    <Box
                      position={showSearch ? ['absolute', 'absolute', 'relative'] : 'relative'}
                      zIndex={showSearch ? 1 : 0}
                      mx={['-16px', '0', 'l']}
                      px={['8px', '0']}
                      flex={[0, 3]}
                      top={0}
                      left={0}
                      right={0}>
                      {showSearch && (
                        <SearchBarWrapper height={[height.mobile, height.desktop]}>
                          <SearchAllBar setShowSearch={setShowSearch} />
                        </SearchBarWrapper>
                      )}
                      {!isMinorSite && (
                        <>
                          <SearchBtnWithTracking
                            role="button"
                            aria-label="search"
                            onClick={() => toggleSearchBar()}
                            position={showSearch ? 'absolute' : null}
                            ml={['6px', '']}
                            mr={['14px', '']}>
                            <IconSearch color={showSearch ? 'grayscale.600' : menuColour} />
                          </SearchBtnWithTracking>
                          <SearchBtnTextWithTracking
                            role="button"
                            aria-label="search"
                            onClick={() => toggleSearchBar()}
                            color={showSearch ? 'grayscale.600' : menuColour}>
                            {searchText}
                          </SearchBtnTextWithTracking>
                        </>
                      )}
                    </Box>
                    {/* flex-basis, not min-width: MenuWrap resets min-width to auto on every descendant. */}
                    <Flex
                      position="relative"
                      justifyContent="flex-end"
                      flexBasis={ACCOUNT_CORNER_WIDTH}
                      flexShrink={0}
                      ml={isTablet && isMinorSite ? 10 : null}>
                      <RefreshingUserMenu
                        userData={{ ...auth }}
                        isLoggedIn={auth.isLoggedIn}
                        transparent={transparent}
                        height={height}
                        logOut={logOut}
                      />
                    </Flex>
                  </>
                )}
              </Flex>
            </Col>
          </Row>
        </Container>
      </MenuWrap>
    </TrackProvider>
  );
};

export default PureMenu;
